import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { useVerticalTiles } from '@/hooks/useVerticalTiles';

/**
 * Story 121.8 + 126.7 — vertical-driven dashboard tiles with live values.
 *
 * Renders the tenant's vertical-specific dashboard tiles (sourced from
 * `/api/v1/verticals/tiles`) as a responsive grid. Each tile's value is
 * resolved by the generic resolver `/api/v1/verticals/tiles/{id}/value`
 * (126.7), which evaluates the tile's `metricQuery` server-side (allowlisted,
 * RLS-scoped). Self-hides when the tenant has no vertical or no tiles.
 */
export function VerticalTileGrid({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { tiles, loading } = useVerticalTiles();

  if (loading) return null;
  if (tiles.length === 0) return null;

  return (
    <div className={className} data-testid="vertical-tile-grid">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
        {tiles.map((tile) => (
          <div
            key={tile.id}
            data-testid={`vertical-tile-${tile.tileKey}`}
            className="rounded-md border border-sidebar-border bg-card p-4"
          >
            <div className="text-xs uppercase text-muted-foreground">{tile.displayType}</div>
            <div className="mt-1 text-sm font-medium">
              {t(tile.titleI18nKey, { defaultValue: tile.tileKey })}
            </div>
            <TileValue tileId={tile.id} displayType={tile.displayType} />
          </div>
        ))}
      </div>
    </div>
  );
}

interface TileValueResponse {
  tileId: string;
  value: number;
  displayType: string;
}

/**
 * TileValue fetches and renders one tile's resolved metric. While loading (or
 * on error) it shows the "—" placeholder so the grid never blocks or breaks on
 * a single tile.
 */
function TileValue({ tileId, displayType }: { tileId: string; displayType: string }) {
  const [value, setValue] = useState<number | null>(null);

  useEffect(() => {
    let active = true;
    apiClient
      .get<TileValueResponse>(`/verticals/tiles/${tileId}/value`)
      .then(({ data }) => {
        if (active) setValue(data.value);
      })
      .catch(() => {
        if (active) setValue(null);
      });
    return () => {
      active = false;
    };
  }, [tileId]);

  return (
    <div className="mt-3 text-2xl font-semibold tabular-nums" data-testid={`tile-value-${tileId}`}>
      {value === null ? '—' : formatTileValue(value, displayType)}
    </div>
  );
}

/** formatTileValue renders a resolved metric per its display type. */
function formatTileValue(value: number, displayType: string): string {
  switch (displayType) {
    case 'PERCENTAGE':
      return `${Math.round(value)}%`;
    case 'SCORE':
      return `${Math.round(value)}`;
    case 'SUM':
      return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
    case 'COUNT':
    case 'TREND':
    default:
      return Math.round(value).toLocaleString();
  }
}
